// Secondary-subdivision (city) selector. The full place list can be ~450 rows,
// many with similar names ("Birmingham", "Brighton", "Brilliant"…), so we use
// fuse.js fuzzy ranking on top of MUI's Autocomplete. The label noun comes
// from settings so it adapts to the region.
import { useEffect, useMemo } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import Fuse from 'fuse.js';
import {
  fetchPlaces, computeAreaStats, setAreaRefId,
} from '../store/area-slice.js';
import { selectSettings } from '../store/settings-slice.js';

const FUSE_OPTIONS = {
  includeScore: false,
  threshold: 0.35,
  ignoreLocation: true,
  keys: ['name'],
};

const CityDropdown = () => {
  const dispatch = useDispatch();
  const { data, loading, error } = useSelector((s) => s.area.places);
  const areaRefId = useSelector((s) => s.area.areaRefId);
  const settings = useSelector(selectSettings);
  const noun = settings.subdivision_secondary?.singular || 'city';

  useEffect(() => {
    if (!data && !loading) dispatch(fetchPlaces());
  }, [data, loading, dispatch]);

  const options = useMemo(() => data || [], [data]);
  const fuse = useMemo(() => new Fuse(options, FUSE_OPTIONS), [options]);

  const selected = useMemo(
    () => options.find((c) => c.id === areaRefId) || null,
    [options, areaRefId],
  );

  // Replace the default substring filter with fuse.js ranking. If the user
  // hasn't typed anything yet, fall back to alphabetical (full list).
  const filterOptions = (allOptions, state) => {
    const q = state.inputValue.trim();
    if (!q) return allOptions;
    return fuse.search(q).map((r) => r.item);
  };

  return (
    <Autocomplete
      options={options}
      value={selected}
      loading={loading}
      filterOptions={filterOptions}
      getOptionLabel={(opt) => opt.name || ''}
      isOptionEqualToValue={(opt, val) => opt.id === val.id}
      onChange={(_e, value) => {
        const nextId = value ? value.id : null;
        dispatch(setAreaRefId(nextId));
        if (nextId != null) dispatch(computeAreaStats({ areaType: 'city', areaRefId: nextId }));
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={`Select ${noun}`}
          placeholder="Type to search…"
          error={Boolean(error)}
          helperText={error || null}
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
};

export default CityDropdown;
